import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Trans, useTranslation } from "react-i18next";

import { FloatingLabelInput, PhoneInput, Columns, Column } from "@aviary";
import { removePlusSign } from "@aviary/components/PhoneInput/PhoneInput";
import { IconWrapper } from "@shared/components/IconWrapper/IconWrapper";
import { StateSelect } from "@shared/components/StateSelect/StateSelect";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import type { PartialAddressInformationData, AddressInformationErrors } from "@shared/types/fields";
import { l } from "@unauthenticated/oauth/locales/i18n";

import * as styles from "./PartialAddressFields.styles";

interface Props {
  addressInformationErrors: AddressInformationErrors;
  addressInformationData: PartialAddressInformationData;
  handleSetAddressInformation: (addressInfo: PartialAddressInformationData) => void;
}

const PartialAddressFields = ({
  addressInformationErrors,
  addressInformationData,
  handleSetAddressInformation,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { isCanada } = useSharedGlobalConfig();

  const isMobile = tablet.lessThan;

  const {
    region: regionErrors,
    postalCode: postalCodeErrors,
    phone: phoneErrors,
  } = addressInformationErrors !== null &&
  !!addressInformationErrors.fields &&
  addressInformationErrors.fields;

  const updateAddressInfo = (id, value) => {
    handleSetAddressInformation({ ...addressInformationData, [id]: value });
  };

  const onAddressChange = e => {
    const value = e.target.value;
    const id = e.target.id;
    updateAddressInfo(id, value);
  };

  const onPhoneNumberChange = value => {
    updateAddressInfo("phone", removePlusSign(value));
  };

  const regionLabel = isCanada ? t(l.oauth.Province) : t(l.oauth.State);

  const postalCodeLabel = isCanada ? t(l.oauth.PostalCode) : t(l.oauth.ZipCode);

  const renderPhoneTooltip = () => {
    return (
      <Trans i18nKey={l.oauth.PhoneTooltip}>
        We're here to help. Providing this information helps our team give our users a{" "}
        <strong>faster</strong> support experience
      </Trans>
    );
  };

  return (
    <Columns isMultiline>
      <Column columnWidth={isMobile ? 12 : 6}>
        <StateSelect
          data-testid="state-select"
          id="region"
          onChange={onAddressChange}
          value={addressInformationData?.region ?? ""}
          label={regionLabel}
          name={regionLabel}
          placeholder={t(l.oauth.Select)}
          isRequired
          errors={regionErrors}
        />
      </Column>
      <Column columnWidth={isMobile ? 12 : 6}>
        <FloatingLabelInput
          type="text"
          id="postalCode"
          label={postalCodeLabel}
          name={postalCodeLabel}
          value={addressInformationData?.postalCode ?? ""}
          handleChange={onAddressChange}
          required
          errors={postalCodeErrors}
        />
      </Column>
      <Column columnWidth={isMobile ? 12 : 6}>
        <IconWrapper icon={faInfoCircle} tooltipMessage={renderPhoneTooltip()}>
          <div css={styles.phoneInput}>
            <PhoneInput
              id="phone"
              label={t(l.oauth.PhoneNumber)}
              name={t(l.oauth.PhoneNumber)}
              aria-label={t(l.oauth.PhoneNumber)}
              value={addressInformationData?.phone ?? ""}
              onChange={onPhoneNumberChange}
              required
              errors={phoneErrors}
              preferredCountryCode={isCanada ? "CA" : "US"}
            />
          </div>
        </IconWrapper>
      </Column>
    </Columns>
  );
};

export { PartialAddressFields, type Props as AddressInfoFieldsProps };
